import React, { useEffect, useRef } from 'react';
import { StyleSheet, Animated, Easing, Dimensions } from 'react-native';
import { useTheme } from 'react-native-paper';
import Svg, {
  Defs,
  LinearGradient,
  Stop,
  Path,
  Circle,
  G,
  Text as SvgText,
} from 'react-native-svg';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

// CoffeePlexity brand colors for loading screen
const LOADING_COLORS = {
  // Theme-matched backgrounds
  backgroundDark: '#1C1B1F',
  backgroundLight: '#F8F6F3',
  // Gold/bronze gradient colors
  goldStart: '#B8860B', // Dark goldenrod
  goldMid: '#DAA520', // Goldenrod
  goldEnd: '#D4A84B', // Light gold
  goldLight: '#E6C97A', // Very light gold
};

export function LoadingScreen() {
  const theme = useTheme();
  const isDark = theme.dark;

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(0.9)).current;
  const textFadeAnim = useRef(new Animated.Value(0)).current;
  const logoFadeAnim = useRef(new Animated.Value(0)).current;

  // Ring rotation animations
  const outerRingRotation = useRef(new Animated.Value(0)).current;
  const innerRingRotation = useRef(new Animated.Value(0)).current;

  // Subtle pulse for the coffee bean
  const beanPulse = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    // Initial entrance animation
    Animated.parallel([
      // Background fade in
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 600,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      // Text fade in with delay
      Animated.sequence([
        Animated.delay(200),
        Animated.timing(textFadeAnim, {
          toValue: 1,
          duration: 800,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
      ]),
      // Logo fade in with delay
      Animated.sequence([
        Animated.delay(400),
        Animated.parallel([
          Animated.timing(logoFadeAnim, {
            toValue: 1,
            duration: 800,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: true,
          }),
          Animated.spring(scaleAnim, {
            toValue: 1,
            tension: 50,
            friction: 8,
            useNativeDriver: true,
          }),
        ]),
      ]),
    ]).start();

    // Continuous outer ring rotation (slow, clockwise)
    Animated.loop(
      Animated.timing(outerRingRotation, {
        toValue: 1,
        duration: 20000,
        easing: Easing.linear,
        useNativeDriver: true,
      })
    ).start();

    // Continuous inner ring rotation (slower, counter-clockwise)
    Animated.loop(
      Animated.timing(innerRingRotation, {
        toValue: -1,
        duration: 25000,
        easing: Easing.linear,
        useNativeDriver: true,
      })
    ).start();

    // Subtle pulse animation for the bean
    Animated.loop(
      Animated.sequence([
        Animated.timing(beanPulse, {
          toValue: 1.02,
          duration: 2000,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(beanPulse, {
          toValue: 1,
          duration: 2000,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ])
    ).start();
  }, [
    fadeAnim,
    textFadeAnim,
    logoFadeAnim,
    scaleAnim,
    outerRingRotation,
    innerRingRotation,
    beanPulse,
  ]);

  const outerRotation = outerRingRotation.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
  });

  const innerRotation = innerRingRotation.interpolate({
    inputRange: [-1, 0],
    outputRange: ['-360deg', '0deg'],
  });

  const textTranslateY = textFadeAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [-20, 0],
  });

  const logoTranslateY = logoFadeAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [30, 0],
  });

  // Use theme-compatible background with CoffeePlexity aesthetics
  const backgroundColor = isDark
    ? LOADING_COLORS.backgroundDark
    : LOADING_COLORS.backgroundLight;

  // Gold colors adapt slightly for light mode
  const goldColors = {
    start: isDark ? LOADING_COLORS.goldStart : '#8B7355',
    mid: isDark ? LOADING_COLORS.goldMid : '#A0845C',
    end: isDark ? LOADING_COLORS.goldEnd : '#B8956E',
    light: isDark ? LOADING_COLORS.goldLight : '#C9A87C',
  };

  const logoSize = Math.min(SCREEN_WIDTH * 0.55, 220);

  return (
    <Animated.View
      style={[
        styles.container,
        {
          backgroundColor,
          opacity: fadeAnim,
        },
      ]}
    >
      {/* CoffeePlexity text at top */}
      <Animated.View
        style={[
          styles.textContainer,
          {
            opacity: textFadeAnim,
            transform: [{ translateY: textTranslateY }],
          },
        ]}
      >
        <Svg height={50} width={SCREEN_WIDTH * 0.8} viewBox='0 0 240 50'>
          <Defs>
            <LinearGradient
              id='textGradient'
              x1='0%'
              y1='0%'
              x2='100%'
              y2='100%'
            >
              <Stop offset='0%' stopColor={goldColors.start} stopOpacity={1} />
              <Stop offset='50%' stopColor={goldColors.mid} stopOpacity={1} />
              <Stop
                offset='100%'
                stopColor={goldColors.light}
                stopOpacity={1}
              />
            </LinearGradient>
          </Defs>
          <SvgText
            fill='url(#textGradient)'
            fontSize='28'
            fontWeight='300'
            x='120'
            y='35'
            textAnchor='middle'
            letterSpacing={1}
          >
            CoffeePlexity
          </SvgText>
        </Svg>
      </Animated.View>

      {/* Main logo with concentric rings and coffee bean */}
      <Animated.View
        style={[
          styles.logoContainer,
          {
            opacity: logoFadeAnim,
            transform: [{ translateY: logoTranslateY }, { scale: scaleAnim }],
          },
        ]}
      >
        {/* Outer rotating ring */}
        <Animated.View
          style={[
            styles.ringContainer,
            {
              width: logoSize,
              height: logoSize,
              transform: [{ rotate: outerRotation }],
            },
          ]}
        >
          <Svg width={logoSize} height={logoSize} viewBox='0 0 200 200'>
            <Defs>
              <LinearGradient
                id='outerRingGradient'
                x1='0%'
                y1='0%'
                x2='100%'
                y2='100%'
              >
                <Stop
                  offset='0%'
                  stopColor={goldColors.start}
                  stopOpacity={0.3}
                />
                <Stop
                  offset='50%'
                  stopColor={goldColors.mid}
                  stopOpacity={0.9}
                />
                <Stop
                  offset='100%'
                  stopColor={goldColors.start}
                  stopOpacity={0.3}
                />
              </LinearGradient>
            </Defs>
            {/* Outer ring - partial circle with gap */}
            <G>
              <Circle
                cx='100'
                cy='100'
                r='94'
                stroke='url(#outerRingGradient)'
                strokeWidth='3'
                fill='none'
                strokeLinecap='round'
                strokeDasharray='450 100'
              />
            </G>
          </Svg>
        </Animated.View>

        {/* Inner rotating ring */}
        <Animated.View
          style={[
            styles.ringContainer,
            styles.absoluteCenter,
            {
              width: logoSize * 0.78,
              height: logoSize * 0.78,
              transform: [{ rotate: innerRotation }],
            },
          ]}
        >
          <Svg
            width={logoSize * 0.78}
            height={logoSize * 0.78}
            viewBox='0 0 160 160'
          >
            <Defs>
              <LinearGradient
                id='innerRingGradient'
                x1='100%'
                y1='0%'
                x2='0%'
                y2='100%'
              >
                <Stop
                  offset='0%'
                  stopColor={goldColors.mid}
                  stopOpacity={0.4}
                />
                <Stop offset='50%' stopColor={goldColors.end} stopOpacity={1} />
                <Stop
                  offset='100%'
                  stopColor={goldColors.mid}
                  stopOpacity={0.4}
                />
              </LinearGradient>
            </Defs>
            <Circle
              cx='80'
              cy='80'
              r='74'
              stroke='url(#innerRingGradient)'
              strokeWidth='2.5'
              fill='none'
              strokeLinecap='round'
              strokeDasharray='380 80'
            />
          </Svg>
        </Animated.View>

        {/* Coffee bean in center */}
        <Animated.View
          style={[
            styles.beanContainer,
            {
              transform: [{ scale: beanPulse }],
            },
          ]}
        >
          <Svg
            width={logoSize * 0.45}
            height={logoSize * 0.55}
            viewBox='0 0 80 100'
          >
            <Defs>
              <LinearGradient
                id='beanGradient'
                x1='0%'
                y1='0%'
                x2='100%'
                y2='100%'
              >
                <Stop
                  offset='0%'
                  stopColor={goldColors.start}
                  stopOpacity={1}
                />
                <Stop offset='40%' stopColor={goldColors.mid} stopOpacity={1} />
                <Stop offset='70%' stopColor={goldColors.end} stopOpacity={1} />
                <Stop
                  offset='100%'
                  stopColor={goldColors.light}
                  stopOpacity={1}
                />
              </LinearGradient>
            </Defs>
            {/* Coffee bean shape - stylized oval with center line */}
            <Path
              d='M40 5
                 C60 5, 75 25, 75 50
                 C75 75, 60 95, 40 95
                 C20 95, 5 75, 5 50
                 C5 25, 20 5, 40 5
                 Z'
              stroke='url(#beanGradient)'
              strokeWidth='3'
              fill='none'
            />
            {/* Center curve of coffee bean */}
            <Path
              d='M40 15
                 C25 35, 25 65, 40 85'
              stroke='url(#beanGradient)'
              strokeWidth='3'
              fill='none'
              strokeLinecap='round'
            />
          </Svg>
        </Animated.View>
      </Animated.View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  textContainer: {
    position: 'absolute',
    top: '22%',
    alignItems: 'center',
  },
  logoContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 40,
  },
  ringContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  absoluteCenter: {
    position: 'absolute',
  },
  beanContainer: {
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
  },
});
